// src/mobile/MobileDemosPage.js
// Mobile Demos / Interactive Studios Page

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Globe, FileSearch, LineChart, Activity,
  X, ExternalLink, ChevronRight, Check,
  Sparkles, RefreshCw, Loader2, Star
} from 'lucide-react';
import MobileNavbar from './MobileNavbar';
import MobileFooter from './MobileFooter';
import MobileBottomNav from './MobileBottomNav';
import { domainFilters, statusFilters } from '../data/studiosData';
import { useStudios } from '../hooks/useStudios';
import './styles/mobile-demos-page.css';

// Icon mapping
const iconMap = {
  Globe,
  FileSearch,
  LineChart,
  Activity
};

// Status colors
const statusColors = {
  'live': '#10B981',
  'in-lab': '#F59E0B',
  'coming-soon': '#6B7280'
};

// Mobile Filter Chip
const FilterChip = ({ label, active, onClick, color }) => (
  <motion.button
    className={`mobile-demos__chip ${active ? 'mobile-demos__chip--active' : ''}`}
    style={active && color ? { '--chip-color': color } : {}}
    onClick={onClick}
    whileTap={{ scale: 0.95 }}
  >
    {label}
  </motion.button>
);

// Mobile Demo Card
const MobileDemoCard = ({ studio, onClick, index }) => {
  const Icon = iconMap[studio.icon] || Globe;
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.article
      className="mobile-demo-card"
      style={{ '--accent': studio.accent }}
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => onClick(studio)}
    >
      <div className="mobile-demo-card__image-wrap">
        {studio.heroScreenshot || studio.thumbnail ? (
          <img
            src={studio.heroScreenshot || studio.thumbnail}
            alt={studio.name}
            className="mobile-demo-card__image"
          />
        ) : (
          <div className="mobile-demo-card__placeholder">
            <Icon size={40} />
          </div>
        )}
      </div>

      <div className="mobile-demo-card__content">
        <div className="mobile-demo-card__header">
          <span
            className={`mobile-demo-card__status mobile-demo-card__status--${studio.status}`}
            style={{ '--status-color': statusColors[studio.status] }}
          >
            {studio.status === 'live' && <span className="mobile-demo-card__status-dot" />}
            {studio.statusLabel}
          </span>
          <span className="mobile-demo-card__domain">{studio.domainLabel}</span>
          {studio.featured && (
            <span className="mobile-demo-card__featured-badge">
              <Star size={10} />
            </span>
          )}
        </div>

        <h3 className="mobile-demo-card__title">{studio.name}</h3>
        <p className="mobile-demo-card__subtitle">{studio.shortDescription}</p>

        <div className="mobile-demo-card__footer">
          <div className="mobile-demo-card__tech">
            {studio.techStack.slice(0, 3).map((tech, i) => (
              <span key={i} className="mobile-demo-card__tech-chip">{tech}</span>
            ))}
            {studio.techStack.length > 3 && (
              <span className="mobile-demo-card__tech-more">+{studio.techStack.length - 3}</span>
            )}
          </div>
          <span className="mobile-demo-card__link">
            View details <ChevronRight size={14} />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

// Mobile Demo Sheet (Full-screen modal)
const MobileDemoSheet = ({ studio, isOpen, onClose }) => {
  const Icon = studio ? (iconMap[studio.icon] || Globe) : Globe;
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!studio) return null;

  const sheetContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="mobile-demo-sheet__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            className="mobile-demo-sheet"
            style={{ '--accent': studio.accent }}
            initial={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
            animate={prefersReducedMotion ? { opacity: 1 } : { y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
            transition={
              prefersReducedMotion
                ? { duration: 0.15 }
                : { type: 'spring', damping: 28, stiffness: 300 }
            }
          >
            {/* Drag Handle */}
            <div className="mobile-demo-sheet__handle">
              <span />
            </div>

            {/* Header */}
            <div className="mobile-demo-sheet__header">
              <div className="mobile-demo-sheet__header-left">
                <h2 className="mobile-demo-sheet__title">{studio.name}</h2>
              </div>
              <div className="mobile-demo-sheet__header-right">
                {studio.featured && (
                  <span className="mobile-demo-sheet__featured-badge">
                    <Star size={10} />
                  </span>
                )}
                <span
                  className={`mobile-demo-sheet__status mobile-demo-sheet__status--${studio.status}`}
                  style={{ '--status-color': statusColors[studio.status] }}
                >
                  {studio.status === 'live' && <span className="mobile-demo-sheet__status-dot" />}
                  {studio.statusLabel}
                </span>
                <button className="mobile-demo-sheet__close" onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="mobile-demo-sheet__body">
              {/* Screenshot */}
              <div className="mobile-demo-sheet__screenshot-wrap">
                {studio.heroScreenshot || studio.thumbnail ? (
                  <img
                    src={studio.heroScreenshot || studio.thumbnail}
                    alt={studio.name}
                    className="mobile-demo-sheet__image"
                  />
                ) : (
                  <div className="mobile-demo-sheet__placeholder">
                    <Icon size={56} />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="mobile-demo-sheet__content">
                <span className="mobile-demo-sheet__domain">{studio.domainLabel}</span>
                <p className="mobile-demo-sheet__description">{studio.longDescription}</p>

                <div className="mobile-demo-sheet__section">
                  <h4>Key Capabilities</h4>
                  <ul className="mobile-demo-sheet__bullets">
                    {studio.bullets.map((bullet, i) => (
                      <li key={i}>
                        <Check size={16} style={{ color: studio.accent }} />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mobile-demo-sheet__section">
                  <h4>Tech Stack</h4>
                  <div className="mobile-demo-sheet__tech-chips">
                    {studio.techStack.map((tech, i) => (
                      <span key={i} className="mobile-demo-sheet__tech-chip">{tech}</span>
                    ))}
                  </div>
                </div>

                <div className="mobile-demo-sheet__tags">
                  {studio.tags.map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sticky CTA Bar */}
            <div className="mobile-demo-sheet__cta-bar">
              {studio.demoUrl ? (
                <a
                  href={studio.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-demo-sheet__btn mobile-demo-sheet__btn--primary"
                >
                  Open Live Demo <ExternalLink size={16} />
                </a>
              ) : (
                <span className="mobile-demo-sheet__btn mobile-demo-sheet__btn--disabled">
                  Coming Soon
                </span>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sheetContent, document.body);
};

// Main MobileDemosPage Component
const MobileDemosPage = () => {
  // Fetch studios from Contentful
  const { studios, loading, error } = useStudios();

  const [activeDomain, setActiveDomain] = useState('all');
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);

  // Filter studios
  const filteredStudios = useMemo(() => {
    return studios
      .filter(s => activeDomain === 'all' || s.domain === activeDomain)
      .filter(s => activeStatuses.length === 0 || activeStatuses.includes(s.status))
      .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  }, [studios, activeDomain, activeStatuses]);

  // Featured studio for hero
  const featuredStudio = useMemo(() => {
    return studios.find(s => s.featured && s.status === 'live') || studios[0];
  }, [studios]);

  // Status counts
  const statusCounts = useMemo(() => ({
    live: studios.filter(s => s.status === 'live').length,
    'in-lab': studios.filter(s => s.status === 'in-lab').length,
    'coming-soon': studios.filter(s => s.status === 'coming-soon').length
  }), [studios]);

  // Sticky detection
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Toggle status filter
  const toggleStatus = (status) => {
    setActiveStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setActiveDomain('all');
    setActiveStatuses([]);
  };

  // Open sheet
  const openSheet = (studio) => {
    setSelectedStudio(studio);
    setIsSheetOpen(true);
  };

  // Close sheet
  const closeSheet = () => {
    setIsSheetOpen(false);
    setTimeout(() => setSelectedStudio(null), 300);
  };

  const hasActiveFilters = activeDomain !== 'all' || activeStatuses.length > 0;
  const Icon = iconMap[featuredStudio?.icon] || Globe;

  // Show loading state
  if (loading) {
    return (
      <div className="mobile-demos-page">
        <MobileNavbar />
        <div className="mobile-demos-page__loading">
          <Loader2 className="mobile-demos-page__spinner" size={28} />
          <p>Loading studios...</p>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="mobile-demos-page">
        <MobileNavbar />
        <div className="mobile-demos-page__error">
          <p>Unable to load studios.</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="mobile-demos-page">
      <MobileNavbar />

      {/* Hero Section */}
      <section className="mobile-demos-hero">
        <div className="mobile-demos-hero__glow" />

        <motion.div
          className="mobile-demos-hero__badge"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Sparkles size={14} />
          Interactive Studios
        </motion.div>

        <motion.h1
          className="mobile-demos-hero__title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          Explore Live Product{' '}
          <span className="mobile-demos-hero__title-gradient">Demos</span>
        </motion.h1>

        <motion.p
          className="mobile-demos-hero__subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Real prototypes solving real industrial problems.
        </motion.p>

        <motion.div
          className="mobile-demos-hero__stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
        >
          <div className="mobile-demos-hero__stat">
            <span className="mobile-demos-hero__stat-dot" style={{ background: statusColors['live'] }} />
            <span>{statusCounts['live']} Live</span>
          </div>
          <div className="mobile-demos-hero__stat">
            <span className="mobile-demos-hero__stat-dot" style={{ background: statusColors['in-lab'] }} />
            <span>{statusCounts['in-lab']} In Lab</span>
          </div>
          <div className="mobile-demos-hero__stat">
            <span className="mobile-demos-hero__stat-dot" style={{ background: statusColors['coming-soon'] }} />
            <span>{statusCounts['coming-soon']} Coming</span>
          </div>
        </motion.div>

        {/* Featured Card */}
        {featuredStudio && (
          <motion.div
            className="mobile-demos-hero__featured"
            style={{ '--accent': featuredStudio.accent }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => openSheet(featuredStudio)}
          >
            <div className="mobile-demos-hero__featured-image">
              {featuredStudio.heroScreenshot || featuredStudio.thumbnail ? (
                <img
                  src={featuredStudio.heroScreenshot || featuredStudio.thumbnail}
                  alt={featuredStudio.name}
                />
              ) : (
                <div className="mobile-demos-hero__featured-placeholder">
                  <Icon size={48} />
                </div>
              )}
            </div>
            <div className="mobile-demos-hero__featured-content">
              <span
                className="mobile-demos-hero__featured-status"
                style={{ '--status-color': statusColors[featuredStudio.status] }}
              >
                {featuredStudio.status === 'live' && <span className="mobile-demos-hero__status-dot" />}
                {featuredStudio.statusLabel}
              </span>
              <h3>{featuredStudio.name}</h3>
              <p>{featuredStudio.tagline}</p>
            </div>
          </motion.div>
        )}
      </section>

      {/* Sentinel for sticky detection */}
      <div ref={sentinelRef} className="mobile-demos__sentinel" />

      {/* Sticky Filters */}
      <div className={`mobile-demos-filters ${isSticky ? 'mobile-demos-filters--sticky' : ''}`}>
        {/* Domain Chips - Horizontal Scroll */}
        <div className="mobile-demos-filters__scroll">
          {domainFilters.map(filter => (
            <FilterChip
              key={filter.id}
              label={filter.label}
              active={activeDomain === filter.id}
              onClick={() => setActiveDomain(filter.id)}
            />
          ))}
        </div>

        {/* Status Chips */}
        <div className="mobile-demos-filters__status">
          {statusFilters.filter(f => f.id !== 'all').map(filter => (
            <FilterChip
              key={filter.id}
              label={filter.label}
              active={activeStatuses.includes(filter.id)}
              onClick={() => toggleStatus(filter.id)}
              color={statusColors[filter.id]}
            />
          ))}
        </div>

        {/* Results Count */}
        <div className="mobile-demos-filters__meta">
          <span className="mobile-demos-filters__count">
            {filteredStudios.length} of {studios.length} studios
          </span>
          {hasActiveFilters && (
            <button className="mobile-demos-filters__clear" onClick={clearFilters}>
              <RefreshCw size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Studio List */}
      <section className="mobile-demos-list">
        <AnimatePresence mode="popLayout">
          {filteredStudios.map((studio, index) => (
            <MobileDemoCard
              key={studio.id}
              studio={studio}
              onClick={openSheet}
              index={index}
            />
          ))}
        </AnimatePresence>

        {filteredStudios.length === 0 && (
          <motion.div
            className="mobile-demos-list__empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>No studios match your filters.</p>
            <button onClick={clearFilters}>
              <RefreshCw size={16} />
              Clear filters
            </button>
          </motion.div>
        )}
      </section>

      {/* Footer */}
      <MobileFooter />

      {/* Bottom Nav Spacer */}
      <div className="mobile-demos-page__nav-spacer" />

      {/* Bottom Navigation */}
      <MobileBottomNav />

      {/* Detail Sheet */}
      <MobileDemoSheet
        studio={selectedStudio}
        isOpen={isSheetOpen}
        onClose={closeSheet}
      />
    </div>
  );
};

export default MobileDemosPage;
